import { useState, useEffect } from "react";
import IntegrationAppCard from "../../../elements/integration_app_card";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import { useLanguage } from "../../../context/LanguageProvider";
import ShopifyLogo from "../../../images/Shopify.png";

export default function Page() {
  const [integrationApps, setIntegrationApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
  setError(null);
  
  try {
    const response = await fetch(BASE_URL+'/api/integrations',{
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(t('integration.apps.error_http', { status: response.status }));
    }
    
    const result = await response.json();
    
    if (result.success) {
      setIntegrationApps(result.data);
    } else {
      throw new Error(result.message || t('integration.apps.error_fetch'));
    }
  } catch (err) {
    console.error('Error fetching integrations:', err);
    setError(err.message);
  } finally {
    setIsLoading(false);
  }
};

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-3">
            Applications d'intégration
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            {t('integration.apps.loading')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden animate-pulse">
              <div className="bg-gradient-to-br from-gray-200 to-gray-300 p-6 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-gray-300"></div>
              </div>
              <div className="p-4">
                <div className="h-6 bg-gray-300 rounded mb-4 mx-auto w-3/4"></div>
                <div className="space-y-3 mb-4">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                </div>
                <div className="h-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-4 pb-12">
        {/* Shopify Landing Page CTA */}
      <section className="mb-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f2e1d] via-[#14532d] to-[#1a6b3c] text-white shadow-2xl">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5 blur-2xl pointer-events-none"></div>
          <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full bg-[#96bf48]/10 blur-3xl pointer-events-none"></div>
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "24px 24px",
            }}
          ></div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-10 p-4 lg:p-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <img src={ShopifyLogo} alt="Shopify" className="w-16 h-16 object-contain drop-shadow" />
                <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-bold bg-white/10 border border-white/20 text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#96bf48] shadow-[0_0_0_3px_rgba(150,191,72,0.3)]"></span>
                  {t('integration.apps.landing_badge')}
                </span>
              </div>

              <h3 className="text-2xl lg:text-3xl font-extrabold mb-1 leading-snug">
                {t('integration.apps.landing_title')}
              </h3>

              <p className="text-sm leading-7 text-white/70 max-w-xl mb-2">
                {t('integration.apps.landing_desc')}
              </p>

              <ul className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs text-white/85">
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-[#96bf48]/20 flex items-center justify-center shrink-0">
                      <svg className="w-2.5 h-2.5 text-[#a8d35c]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    {t(`integration.apps.landing_feature_${i}`)}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center lg:items-end gap-4 shrink-0">
              <a
                href="https://apps.shopify.com/webi-landing-page-builder-cod"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-2xl px-7 py-4 bg-[#96bf48] text-[#10221b] font-extrabold text-sm hover:bg-[#a8d35c] hover:shadow-2xl hover:shadow-[#96bf48]/30 transition-all active:scale-95"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                {t('integration.apps.landing_cta')}
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <span className="text-[11px] text-white/50 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {t('integration.apps.landing_note')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-3">
              {t('integration.apps.title')}
            </h1>
            <p className="text-sm text-gray-600 max-w-2xl">
              {t('integration.apps.subtitle')}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {error && (
              <div className="text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                {t('integration.apps.api_error')}
              </div>
            )}
            
            <button
              onClick={fetchApps}
              className="flex cursor-pointer items-center gap-2 text-xs py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('integration.apps.refresh')}
            </button>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          <p>{t('integration.apps.stats', { total: integrationApps.length, active: integrationApps.filter(a => a.status === 'true').length, inactive: integrationApps.filter(a => a.status === 'false').length })}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-yellow-700">
              {t('integration.apps.data_may_be_outdated', { error })}
            </span>
          </div>
        </div>
      )}

      {/* Cards Grid */}
      {integrationApps.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('integration.apps.not_found')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('integration.apps.not_found_desc')}
          </p>
          <button
            onClick={fetchApps}
            className="inline-flex items-center gap-2 text-sm py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('integration.apps.retry')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-8">
          {integrationApps.map((app) => (
            <IntegrationAppCard 
              key={app.name} 
              app={app} 
              fetchApps={fetchApps}
            />
          ))}
        </div>
      )}

    </div>
  );
}

